/**
 * Route path constants — type-safe, avoids magic strings
 */
export const ROUTES = {
  root: '/',
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  acceptInvite: '/accept-invite',
  changePassword: '/change-password',
  verify2fa: '/verify-2fa',
  mfaEnroll: '/mfa/enroll',

  dashboard: '/dashboard',

  personnel: {
    root: '/personnel',
    employees: '/personnel/employees',
    employeeDetail: (id: string) => `/personnel/employees/${id}`,
    employmentHistory: '/personnel/employment-history',
    employmentList: '/personnel/employment-list',
    deductionList: '/personnel/deduction-list',
    deductionsOverview: '/personnel/deductions-overview',
    leaveManagement: '/personnel/leave-management',
  },

  payroll: {
    root: '/payroll',
    cycles: '/payroll/cycles',
    cycleDetail: (id: string) => `/payroll/cycles/${id}`,
    cycleWorkerEstates: (cycleId: string, employeeId: string) =>
      `/payroll/cycles/${cycleId}/workers/${employeeId}`,
    cycleWorkerEstateTasks: (cycleId: string, employeeId: string, estateId: string) =>
      `/payroll/cycles/${cycleId}/workers/${employeeId}/estates/${estateId}`,
    millingBatches: '/payroll/milling-batches',
    millingBatchDetail: (id: string) => `/payroll/milling-batches/${id}`,
    cpkoInventory: '/payroll/cpko-inventory',
    pkcInventory:  '/payroll/pkc-inventory',
    operationalExpenses: '/payroll/operational-expenses',
    loanDeductions: '/payroll/loan-deductions',
    farmSales: '/payroll/farm-sales',
    farmSalesOverview: '/payroll/farm-sales-overview',
    farmSaleConfig: '/payroll/farm-sale-config',
    prepaymentFunds: '/payroll/prepayment-funds',
  },

  estates: {
    root: '/estates',
    list: '/estates',
    detail: (id: string) => `/estates/${id}`,
    blocks: '/estates/blocks',
    operationTypes: '/estates/operation-types',
    operationRates: '/estates/operation-rates',
    averageWeights: '/estates/average-weights',
  },

  inventory: {
    root: '/inventory',
    items: '/inventory/items',
    itemDetail: (id: string) => `/inventory/items/${id}`,
    itemCategories: '/inventory/item-categories',
    vendors: '/inventory/vendors',
    vendorDetail: (id: string) => `/inventory/vendors/${id}`,
    stock: '/inventory/stock',
    stores: '/inventory/stores',
    procurements: '/inventory/procurement',
    procurementDetail: (id: string) => `/inventory/procurement/${id}`,
    requisitions: '/inventory/requisitions',
    requisitionDetail: (id: string) => `/inventory/requisitions/${id}`,
  },

  sales: {
    root: '/sales',
    oilPurchases: '/sales/oil-purchases',
    oilPurchaseDetail: (id: string) => `/sales/oil-purchases/${id}`,
    saleOrders: '/sales/sale-orders',
    saleOrderDetail: (id: string) => `/sales/sale-orders/${id}`,
    saleTypes: '/sales/sale-types',
    pricingTiers: '/sales/pricing-tiers',
    salePrices: '/sales/sale-prices',
    marketPrices: '/sales/market-prices',
    dashboard: '/sales/dashboard',
    oilAllocations: '/sales/oil-allocations',
    oilAllocationDetail: (id: string) => `/sales/oil-allocations/${id}`,
    oilSterilizations: '/sales/oil-sterilizations',
    oilSterilizationDetail: (id: string) => `/sales/oil-sterilizations/${id}`,
    finishedGoods: '/sales/finished-goods',
    cpoInventory:  '/sales/inventory',
    customers: '/sales/customers',
    skus: '/sales/skus',
    bottlingBatches: '/sales/bottling-batches',
    bottlingBatchDetail: (id: string) => `/sales/bottling-batches/${id}`,
    cartonningBatches: '/sales/cartonning-batches',
    cartonningBatchDetail: (id: string) => `/sales/cartonning-batches/${id}`,
  },

  geography: {
    root: '/geography',
    countries: '/geography/countries',
    regions: '/geography/regions',
    districts: '/geography/districts',
  },

  administration: {
    root: '/administration',
    users: '/administration/users',
    roles: '/administration/roles',
    workingYears: '/administration/working-years',
  },

  setup: {
    root: '/setup',
    deductionTypes: '/setup/deduction-types',
    allowanceTypes: '/setup/allowance-types',
    salaryGrid: '/setup/salary-grid',
  },

  settings: {
    root: '/settings',
    payrollSettings: '/settings/payroll-settings',
    salaryItems: '/settings/salary-items',
    momoOperators: '/settings/momo-operators',
    mobileProvision: '/settings/mobile-provision',
    statutoryRates: '/settings/statutory-rates',
    security: '/settings/security',
    systemSettings: '/settings/system-settings',
  },

  reporting: {
    root: '/reporting',
    // Payroll reports
    payrollSummary: '/reporting/payroll/payroll-summary',
    payslip: '/reporting/payroll/payslip',
    taskDetail: '/reporting/payroll/task-detail',
    millingReport: '/reporting/payroll/milling-report',
    operationRatesCard: '/reporting/payroll/operation-rates-card',
    productionReport: '/reporting/payroll/production-report',
    costOfProduction: '/reporting/payroll/cost-of-production',
    labourProductivity: '/reporting/payroll/labour-productivity',
    blockPerformance: '/reporting/payroll/block-performance',
    annualSummary: '/reporting/payroll/annual-summary',
    // Personnel reports
    employeeRegister: '/reporting/personnel/employee-register',
    headcount: '/reporting/personnel/headcount',
    advancesLoans: '/reporting/personnel/advances-loans',
    deductionAging: '/reporting/personnel/deduction-aging',
    employmentHistoryReport: '/reporting/personnel/employment-history',
    // Estates reports
    estateProfile: '/reporting/estates/estate-profile',
    blockProductivity: '/reporting/estates/block-productivity',
    operationPerformance: '/reporting/estates/operation-performance',
    // Inventory reports
    stockLevels: '/reporting/inventory/stock-levels',
    stockMovement: '/reporting/inventory/stock-movement',
    procurementReport: '/reporting/inventory/procurement',
    requisitionsReport: '/reporting/inventory/requisitions',
    vendorSummary: '/reporting/inventory/vendor-summary',
    // Expenses reports
    expenseSummary: '/reporting/expenses/expense-summary',
    expenseDetail: '/reporting/expenses/expense-detail',
    expenseByEstate: '/reporting/expenses/expense-by-estate',
    // Validated-cycle history (previously managed data, now read-only)
    farmSalesHistory:      '/reporting/payroll/farm-sales-history',
    cycleDeductions:       '/reporting/payroll/cycle-deductions',
    prepaymentsHistory:    '/reporting/payroll/prepayments-history',
    prepaymentAudit:       '/reporting/payroll/prepayment-audit',
    // Sales reports
    salesInventoryBalances: '/reporting/sales/inventory-balances',
    productionVsAllocation: '/reporting/sales/production-vs-allocation',
    bottlingEfficiency: '/reporting/sales/bottling-efficiency',
    rejectionRate: '/reporting/sales/rejection-rate',
    marginByChannel: '/reporting/sales/margin-by-channel',
    fgTurnover: '/reporting/sales/fg-turnover',
    oilPurchaseReport: '/sales/reports/oil-purchases',
    marketPriceStrategy: '/sales/reports/market-price-strategy',
    // Legacy
    payrollReport: '/reporting/payroll',
    expenseReport: '/reporting/expenses',
  },

  notFound: '*',
} as const;
